import {createApi} from '@reduxjs/toolkit/query/react'
import {baseQuery} from '../base-query'

export interface SiteRef {
  id: string
  name: string
}

export interface PurchaseOrderResponse {
  id: string
  poNumber: string
  companyId: string
  poAmount: number
  amountUsed: number
  remainingAmount: number
  description?: string
  active: boolean
  siteIds?: string[]
  sites?: SiteRef[]
  versionKey?: number
  createdDate?: string
  modifiedDate?: string
}

export interface PurchaseOrderPagingResponse {
  data: PurchaseOrderResponse[]
  pageNumber: number
  pageSize: number
  totalCount: number
  totalPages: number
}

export interface GetPurchaseOrdersParams {
  companyId: string
  pageNumber?: number
  pageSize?: number
  search?: string
}

export interface CreatePurchaseOrderRequest {
  companyId: string
  poNumber: string
  poAmount?: number
  description?: string
  active?: boolean
  siteIds?: string[]
}

export interface UpdatePurchaseOrderRequest {
  _id: string
  poNumber?: string
  poAmount?: number
  description?: string
  active?: boolean
  siteIds?: string[]
}

export const purchaseOrderApi = createApi({
  reducerPath: 'PurchaseOrderApi',
  tagTypes: ['PurchaseOrder'],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getPurchaseOrders: builder.query<PurchaseOrderPagingResponse, GetPurchaseOrdersParams>({
      providesTags: ['PurchaseOrder'],
      query: ({companyId, pageNumber = 1, pageSize = 50, search}) => ({
        url: `/purchase-order/getPurchaseOrders/${companyId}`,
        method: 'GET',
        params: {
          pageNumber,
          pageSize,
          ...(search && {search}),
        },
      }),
    }),
    getActivePurchaseOrders: builder.query<PurchaseOrderResponse[], string>({
      providesTags: ['PurchaseOrder'],
      query: (companyId) => ({
        url: `/purchase-order/getActivePurchaseOrders/${companyId}`,
        method: 'GET',
      }),
    }),
    createPurchaseOrder: builder.mutation<PurchaseOrderResponse, CreatePurchaseOrderRequest>({
      invalidatesTags: ['PurchaseOrder'],
      query: ({companyId, ...body}) => ({
        url: `/purchase-order/createPurchaseOrder/${companyId}`,
        method: 'POST',
        body,
      }),
    }),
    updatePurchaseOrder: builder.mutation<PurchaseOrderResponse, UpdatePurchaseOrderRequest>({
      invalidatesTags: ['PurchaseOrder'],
      query: (body) => ({
        url: `/purchase-order/updatePurchaseOrder`,
        method: 'PUT',
        body,
      }),
    }),
    recalculatePurchaseOrders: builder.mutation<{success: boolean}, string>({
      invalidatesTags: ['PurchaseOrder'],
      query: (companyId) => ({
        url: `/purchase-order/recalculate/${companyId}`,
        method: 'PUT',
      }),
    }),
  }),
})

export const {
  useGetPurchaseOrdersQuery,
  useLazyGetPurchaseOrdersQuery,
  useGetActivePurchaseOrdersQuery,
  useCreatePurchaseOrderMutation,
  useUpdatePurchaseOrderMutation,
  useRecalculatePurchaseOrdersMutation,
} = purchaseOrderApi
